import { useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import dxTreeView, { ItemClickEvent } from "devextreme/ui/tree_view";
import { SnackbarMessage } from "../../hooks/useSnackbar.types";
// import { useGetURL, useSnackbar } from "../../hooks";
import { useSnackbar } from "../../hooks";
//import { refreshFlagAtom } from "../../constants";
import { taxPortalClientApi, createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { Descriptions } from "../../api-client-nswag/taxportal-client";
import { IsFileManagementMicroserviceEnabled } from "../../envVariables";
import {
  IManageFilesDTO,
  IManageFilesState,
  IManageFilesVars,
  IManageEntityItem,
} from "./IManageFilesTypes";

import "./IManageFiles.scss";
import IManageFiles from "./IManageFiles";

const { IMANAGE_FILES, TAXPORTAL_FOLDERS } = Descriptions;
const initialStateIManageFiles = { pending: false, fulfilled: false, data: [] };

const addUiKeys = (nodes: IManageFilesDTO[], parentKey = "root"): IManageFilesDTO[] =>
  nodes.map((node, index) => {
    const uniqueId = node.uniqueId ?? `${parentKey}-${index}-${node.id ?? node.text ?? "node"}`;
    return {
      ...node,
      uniqueId,
      items: node.items ? addUiKeys(node.items, uniqueId) : node.items,
    };
  });

const collectEntities = (nodes: IManageFilesDTO[]): IManageEntityItem[] => {
  const results: IManageEntityItem[] = [];
  const traverse = (items: IManageFilesDTO[]) => {
    for (const node of items) {
      if (node.level === 2 && node.id && node.uniqueId && node.text) {
        results.push({ id: node.id, uniqueId: node.uniqueId, text: node.text });
      }
      if (node.items?.length) traverse(node.items);
    }
  };
  traverse(nodes);
  return results;
};

export default function IManageFilesContainer() {
  //const siteID = useGetURL();
  //const refreshFlag = useRecoilValue(refreshFlagAtom);
  const { showSnackbar } = useSnackbar();
  const [filesToMove, setFilesToMove] = useState<IManageFilesState>(initialStateIManageFiles);
  const [foldersToShow, setFoldersToShow] = useState<IManageFilesState>(initialStateIManageFiles);
  const siteID = sessionStorage.getItem("siteId") as string;
  const [isSource, setIsSource] = useState(false);
  const [isTPSource, setIsTPSource] = useState(false);
  const [isTargetRefreshing, setIsTargetRefreshing] = useState<string | null>(null);
  const [expandedWorkspaceEntityId, setExpandedWorkspaceEntityId] = useState<string | null>(null);
  const isFileManagementMicroserviceEnabled = IsFileManagementMicroserviceEnabled.toLowerCase();
  const entities = useMemo<IManageEntityItem[]>(
    () => collectEntities(filesToMove.data),
    [filesToMove.data]
  );

  const fetchTreeItems = async (type: Descriptions, setData: Function, isSource: boolean) => {
    setData({ ...initialStateIManageFiles, pending: true });
    if (siteID) {
      try {
        const parsedSiteId = parseInt(siteID);
        if (type === IMANAGE_FILES) {
          if (isFileManagementMicroserviceEnabled === "true") {
            const response = await createSitesClientApi.hqFolder_GetWorkspaces(parsedSiteId, undefined, undefined, isSource);
            const rootNodes = addUiKeys(response.data?.files ?? []);
            setData({ ...initialStateIManageFiles, fulfilled: true, data: rootNodes });
          } else {
            const response = await taxPortalClientApi.taxPortal_GetIManageFiles(
            parseInt(siteID),
            undefined,
            undefined,
            isSource
          );
          let rootNodes = addUiKeys(response?.files ?? []);
          setData({ ...initialStateIManageFiles, fulfilled: true, data: rootNodes });
          }
        } else {
          if (isFileManagementMicroserviceEnabled === "true") {
            const response = await createSitesClientApi.hqFolder_GetFoldersBySiteId(parsedSiteId, isSource);
            setData({ ...initialStateIManageFiles, fulfilled: true, data: addUiKeys(response.data?.files ?? []) });
          } else {
            const newData = await taxPortalClientApi.taxPortal_GetDirectories(type, parseInt(siteID), isSource);
          setData({ ...initialStateIManageFiles, fulfilled: true, data: addUiKeys(newData.files ?? []) });
}
        }
      } catch (error) {
        showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        setData(initialStateIManageFiles);
      }
    } else {
      setData(initialStateIManageFiles);
    }
  };

  const findIdx = (arr: IManageFilesDTO[], node: IManageFilesDTO) => {
    return arr.findIndex((object: any) => {
      return node.uniqueId && object.uniqueId ? object.uniqueId === node.uniqueId : object.id === node.id;
    });
  };

  const handleSourceBool = (isSourceValue: any, isTPSourceValue: any) => {
    setIsSource(isSourceValue);
    setIsTPSource(isTPSourceValue);
  };

  const getChildren = (level: number, node: any, data: IManageFilesDTO[], newData: IManageFilesDTO[]) => {
    const selected = node.itemData ? { ...node.itemData } : null;

    let nodes = [selected];
    let nestedNodes = "parent";
    for (let i = level; i > 1; i--) {
      const prnt = nestedNodes.split(".").reduce((prev, cur) => prev[cur], node);
      nodes.push({ ...prnt.itemData });
      nestedNodes += ".parent";
    }

    let pushData = newData;
    const numberToTake =
      newData[0] && newData[0].showMoreLimit ? newData[0].showMoreLimit : IManageFilesVars.SHOW_MORE_MIN_LIMIT;
    if (level === 2 && newData.length > numberToTake) {
      pushData = showMoreData(newData, numberToTake!);
    }

    nodes[0].items = pushData;
    nodes.forEach((obj, ind) => {
      if (nodes[ind + 1]) {
        const idx = findIdx(nodes[ind + 1].items, obj);
        nodes[ind + 1].items[idx] = obj;
      } else {
        const idx = findIdx(data, obj);
        data[idx] = obj;
      }
      nodes.shift();
    });

    return data as IManageFilesDTO[];
  };

  const showMoreData = (data: IManageFilesDTO[], take: number) => {
    const firstData = data.slice(0, take);
    const newData = [
      ...firstData,
      {
        text: "Show More",
        id: `show-more-${data[0].id}`,
        hasItems: true,
        itemType: "folder",
        items: data.slice(take, data.length),
      },
    ];
    return newData;
  };

  const getIManageData = async (
    treeView: any,
    level: number,
    node?: any,
    clientId?: string,
    workspaceId?: string,
    isSource?: boolean
  ) => {
    if (siteID) {
      try {
        const currData = filesToMove.data;
        let newData: IManageFilesDTO[];
        if (isFileManagementMicroserviceEnabled === "true") {
          const response = await createSitesClientApi.hqFolder_GetWorkspaces(
            parseInt(siteID),
            clientId ?? undefined,
            workspaceId ?? undefined,
            isSource
          );
          newData = addUiKeys(response.data?.files ?? [], node.itemData?.uniqueId ?? node.key ?? node.itemData?.id ?? "root");
        } else {
          const response = await taxPortalClientApi.taxPortal_GetIManageFiles(
            parseInt(siteID),
            clientId,
            workspaceId,
            isSource
          );
          newData = addUiKeys(response?.files ?? [], node.itemData?.uniqueId ?? node.key ?? node.itemData?.id ?? "root");
        }
        const message = SnackbarMessage.IMANAGE_FILES_NO_DATA.replace(
          "%item%",
          node.itemData.text
        ) as SnackbarMessage;
        if (!newData.length) showSnackbar({ message: message, severity: "info" });
        newData = getChildren(level, node, currData, newData) as IManageFilesDTO[];
        treeView.option("dataSource", newData);
        // If this was a workspace expand (level >= 3), capture the parent entity id
        if (workspaceId && clientId) {
          setExpandedWorkspaceEntityId(clientId);
        }
        setFilesToMove({
          ...initialStateIManageFiles,
          fulfilled: true,
          data: newData,
        });
      } catch (error) {
        showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        setFilesToMove(initialStateIManageFiles);
      }
    }
  };

  const settingLoadingLabel = (element: HTMLElement) => {
    let emptyNode: HTMLElement | null = element.querySelector("ul > li ")!.querySelector(".dx-item-content > span");
    if (emptyNode!.innerText === "") emptyNode!.innerText = "Loading...";
  };

  const showMoreLabel = (element: ItemClickEvent<any>) => {
    const span = element.itemElement?.querySelector("span");

    if (!span) {
      return; 
    }

    const text = span.innerText?.trim();

    if (text === "Show More") {
      span.innerHTML = "<b>Hide</b>";
    } else if (text === "Hide") {
      span.innerHTML = "<b>Show More</b>";
    }
  };

  const onItemExpanded = (e: ItemClickEvent<any>) => {
    showMoreLabel(e);
    const item = e.itemData;
    const level = parseInt(item!.level);
    const treeViewEl = e.element;
    const treeView = dxTreeView.getInstance(treeViewEl);

    if (item!.items![0].text === "" && item!.items![0].disabled === true) {
      if (e.itemElement!.parentElement) settingLoadingLabel(e.itemElement!.parentElement);
      let clientId = item!.id as string,
        workspaceId = "";
      if (level >= 3) {
        clientId = item!.parentId as string;
        workspaceId = item!.id as string;
      }
      getIManageData(treeView, level, e.node, clientId, workspaceId, isSource);
    }
  };

  useEffect(() => {
    setFilesToMove(initialStateIManageFiles);
    setFoldersToShow(initialStateIManageFiles);
    fetchTreeItems(IMANAGE_FILES, setFilesToMove, isTPSource);
    fetchTreeItems(TAXPORTAL_FOLDERS, setFoldersToShow, isTPSource);
  }, [siteID, isTPSource]);

  useEffect(() => {
    if (isTargetRefreshing === TAXPORTAL_FOLDERS) {
      setFoldersToShow(initialStateIManageFiles);
      fetchTreeItems(TAXPORTAL_FOLDERS, setFoldersToShow, isTPSource);
    } else if (isTargetRefreshing === IMANAGE_FILES) {
      setFilesToMove(initialStateIManageFiles);
      fetchTreeItems(IMANAGE_FILES, setFilesToMove, isTPSource);
    }
  }, [isTargetRefreshing]);

  return (
    <IManageFiles
      onItemExpanded={onItemExpanded}
      showMoreLabel={showMoreLabel}
      filesToMove={filesToMove}
      foldersToShow={foldersToShow}
      siteID={parseInt(siteID)}
      onSourceBoolChange={handleSourceBool}
      refresh={setIsTargetRefreshing}
      entities={entities}
      onWorkspaceExpanded={setExpandedWorkspaceEntityId}
      expandedWorkspaceEntityId={expandedWorkspaceEntityId}
    />
  );
}
